import type { AxiosInstance } from 'axios';

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  isSuspicious: boolean;
  blocked: boolean;
  verified: boolean;
  packageName: string | null;
  walletBalancePaise: number;
  createdAt: string;
}

export interface ClassifiedPackage {
  id: string;
  name: string;
  description: string | null;
  pricePaise: number;
  durationDays: number;
  adLimit: number;
  active: boolean;
  createdAt: string;
}

export type ClassifiedStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ClassifiedProduct {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  pricePaise: number;
  uploadedById: string;
  uploadedByName: string | null;
  status: ClassifiedStatus;
  published: boolean;
  createdAt: string;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function customerEndpoints(http: AxiosInstance) {
  return {
    // Customers
    list: (params?: { q?: string; verification?: 'VERIFIED' | 'UNVERIFIED' }) =>
      unwrap<Customer[]>(http.get('/admin/customers', { params })),
    create: (body: { name: string; email: string; phone: string; password: string; classifiedPackageId?: string }) =>
      http.post<{ id: string }>('/admin/customers', body).then((r) => r.data),
    update: (
      customerId: string,
      body: Partial<{ name: string; phone: string; isSuspicious: boolean; blocked: boolean; classifiedPackageId: string | null }>,
    ) => http.patch(`/admin/customers/${customerId}`, body).then((r) => r.data),
    bulkUpdate: (body: Partial<{ name: string; phone: string; isSuspicious: boolean; blocked: boolean; classifiedPackageId: string | null }> & { ids: string[] }) => 
      http.post<{ successful: number; failed: number; errors: string[] }>('/admin/customers/bulk-update', body).then((r) => r.data),
    remove: (customerId: string) => http.delete(`/admin/customers/${customerId}`).then((r) => r.data),
    get360: (customerId: string) =>
      unwrap<import('@terra/contracts').customers.Customer360Response>(http.get(`/admin/customers/${customerId}/360`)),

    // Classified packages
    listPackages: () => unwrap<ClassifiedPackage[]>(http.get('/admin/classified/packages')),
    createPackage: (body: {
      name: string;
      description?: string;
      pricePaise: number;
      durationDays?: number;
      adLimit?: number;
      active?: boolean;
    }) => http.post<ClassifiedPackage>('/admin/classified/packages', body).then((r) => r.data),
    updatePackage: (
      packageId: string,
      body: { name: string; description?: string; pricePaise: number; durationDays?: number; adLimit?: number; active?: boolean },
    ) => http.patch<ClassifiedPackage>(`/admin/classified/packages/${packageId}`, body).then((r) => r.data),
    removePackage: (packageId: string) =>
      http.delete(`/admin/classified/packages/${packageId}`).then((r) => r.data),

    // Classified products
    listClassifiedProducts: (params?: { q?: string; status?: ClassifiedStatus }) =>
      unwrap<ClassifiedProduct[]>(http.get('/admin/classified/products', { params })),
    updateClassifiedProduct: (productId: string, body: { status?: ClassifiedStatus; published?: boolean }) =>
      http.patch<ClassifiedProduct>(`/admin/classified/products/${productId}`, body).then((r) => r.data),
    removeClassifiedProduct: (productId: string) =>
      http.delete(`/admin/classified/products/${productId}`).then((r) => r.data),
  };
}

export type CustomerEndpoints = ReturnType<typeof customerEndpoints>;
